import personImage from "../../../common/images/person.svg";
import refreshImage from "../../../common/images/refresh-blue.svg";
import bookImage from "../../../common/images/book.svg";
import { PrimaryTableRow } from "@hatch-digital-nl/abs-storybook/legacy";
import { Dispatch } from "redux";
import { format, parseISO } from "date-fns";
import { ExpiredCertificateInterface } from "../../../common/interfaces/ExpiredCertificateInterface";
import { NavigateFunction } from "react-router";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import { handleOnDownloadClick } from "../../../common/utils/handleOnDownloadClick";
import {
    setIsCreateCertificateModalOpen,
    setSelectedEmployee,
    setSelectedQualification,
} from "../../../modals/CreateCertificateModal/CreateCertificateModalSlice";
import { openFileInNewTab } from "../../../common/utils/openFileInNewTab";
import { EmployeeInterface } from "../../../common/interfaces/EmployeeInterface";
import { QualificationInterface } from "../../../common/interfaces/QualificationInterface";
import { QualificationTypeEnum } from "../../../common/enums/QualificationTypeEnum";
import { QualificationValidityTypeEnum } from "../../../common/enums/QualificationValidityTypeEnum";

interface Props {
    certificates: ExpiredCertificateInterface[];
    dispatch: Dispatch;
    navigate: NavigateFunction;
    isEstablishmentManager?: boolean;
}

const formatExpiryDate = (expiresAt: string | null): string => {
    if (!expiresAt) {
        return "";
    }

    try {
        return format(parseISO(expiresAt), "dd-MM-yyyy");
    } catch {
        return "";
    }
};

const toSelectedEmployee = (
    certificate: ExpiredCertificateInterface,
): EmployeeInterface =>
    ({
        id: certificate.employee_id,
        user: {
            id: 0,
            first_name: certificate.holder_name ?? "",
            last_name: "",
            private_email: "",
        },
    }) as EmployeeInterface;

const toSelectedQualification = (
    certificate: ExpiredCertificateInterface,
): QualificationInterface =>
    ({
        id: certificate.qualification_id,
        name: certificate.qualification_name,
        educators: [],
        image_url: null,
        certificate_count: 0,
        function_group: [],
        qualification_standards: [],
        validity_type: QualificationValidityTypeEnum.PERIOD,
        validity_period: null,
        fixed_valid_until: null,
        qualification_exemptions: [],
        created_at: "",
        type: QualificationTypeEnum.PERSON,
        requires_multiple_achievements: false,
    }) as QualificationInterface;

export const getExpiredTableRows = (props: Props): PrimaryTableRow[][] => {
    const { certificates, navigate, dispatch, isEstablishmentManager } = props;

    return certificates.map((certificate): PrimaryTableRow[] => {
        return [
            {
                value: certificate.qualification_name,
            },
            ...(isEstablishmentManager
                ? [
                      {
                          value: certificate.holder_name ?? "-",
                      },
                      {
                          value: certificate.qualification_norms?.length
                              ? certificate.qualification_norms
                              : "",
                      },
                      {
                          value: certificate.educator_name ?? "-",
                      },
                  ]
                : []),
            {
                value: formatExpiryDate(certificate.expires_at),
            },
            {
                value: "",
                actionData: [
                    {
                        title: isEstablishmentManager
                            ? "Certificaat bekijken"
                            : "Certificaat downloaden",
                        iconUrl: bookImage,
                        onClick: () => {
                            if (!certificate.certificate_url) {
                                return;
                            }

                            if (isEstablishmentManager) {
                                openFileInNewTab(certificate.certificate_url);
                                return;
                            }

                            handleOnDownloadClick(
                                certificate.certificate_url,
                            ).catch((error) => {
                                console.error(error);
                            });
                        },
                    },
                    ...(isEstablishmentManager && certificate.employee_id
                        ? [
                              {
                                  title: "Medewerker bekijken",
                                  iconUrl: personImage,
                                  onClick: () => {
                                      navigate(
                                          RouteEnum.EMPLOYEES +
                                              `/${certificate.employee_id}`,
                                      );
                                  },
                              },
                          ]
                        : []),
                    {
                        title: "Opnieuw toevoegen",
                        iconUrl: refreshImage,
                        onClick: () => {
                            if (certificate.employee_id) {
                                dispatch(
                                    setSelectedEmployee(
                                        toSelectedEmployee(certificate),
                                    ),
                                );
                            }
                            dispatch(
                                setSelectedQualification(
                                    toSelectedQualification(certificate),
                                ),
                            );
                            dispatch(setIsCreateCertificateModalOpen(true));
                        },
                    },
                ],
            },
        ];
    });
};
